import React, { useEffect, useState } from 'react';
import { user as userActions } from '../../actions';
import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import CircularProgress from '@material-ui/core/CircularProgress';
import {
  AccordionSummary,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@material-ui/core';
import { Language } from '../../utils';
import makeClasses from './styles';
import { useSelector, useDispatch } from 'react-redux';
import CancelOutlinedIcon from '@material-ui/icons/CancelOutlined';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import clsx from 'clsx';
import Markdown from 'markdown-to-jsx';
import { Utils } from '../../utils';

const NewPlansModal = ({ open, branch, onClose, hasRUT = false }) => {
  const classes = makeClasses();
  const dispatch = useDispatch();
  const [loading, setIsLoading] = useState(false)
  const [plan, setPlan] = useState(null)
  const [coefficientData, setCoefficientData] = useState(null)
  const [additionalServiceData, setAdditionalServiceData] = useState(null)
  const { languageTexts } = useSelector((state) => ({
    languageTexts: state.language.texts || {},
  }));
  const i18n = Language(languageTexts);

  useEffect(() => {
    const plan = branch?.plan.labelId.split(':')[1];
    const docType = hasRUT ? 'RUT' : 'CI';
    const completeKey = `${plan}+${docType}`;
    if (open) {
      setIsLoading(true);
      dispatch(userActions.getPaymentPlan(completeKey, (data) => {
        setPlan(data)
        setIsLoading(false);
      }));
      dispatch(userActions.getPaymentSurchagesCoefficients((data) => {
        setCoefficientData(data);
      }));
      dispatch(userActions.getPaymentPlanAdditionalService((data) => {
        setAdditionalServiceData(data?.additionalServices);
      }));
    } else {
      setPlan(null);
    }
  }, [open])


  const organizedCoefficientData = coefficientData?.flatMap(c => c.coefficients.map(item => ({ ...item, currency: c.currency })))
  .reduce((acc, { installments, coefficient, currency }) => {
    let row = acc.find(r => r.installments === installments);
    if (!row) {
      row = { installments };
      acc.push(row);
    }
    row[currency] = coefficient;
    return acc;
  }, []);

  const renderGroup = (line) => {
    const organizedLine = groupItems(line)
    return <div key={organizedLine.group}>
          <div className={classes.plansGroupTitle}>{renderGroupName(organizedLine.group)}</div>
          <div>
            {organizedLine?.items?.map((item, index) => {
              return <div key={index} className={classes.field}>
                <div className={classes.plansGroupItem}>
                  <div className={classes.plansGroupName}>
                    <div className={classes.plansGroupLine}>{renderPlanName(item.line)}</div>
                    <div><span>{i18n.get('Components.PlansModalV2.PlanIvaLabel')}</span>{' '}<span className={classes.plansGroupLine}>{calculateFee(item.fee)}%</span></div>
                  </div>
                  <div><span>{i18n.get('Components.PlansModalV2.GetPayedLabel')}</span>{' '}<span className={classes.plansGroupLine}>{renderDay(item.paymentTerm)}</span></div>
                </div>
              </div>;
            })}
          </div>
        </div>
  }
  
const groupItems = (line) => {
  const pagoDespuesGroup = "Pago Después";
  const creditGroup = "Credit";
  const creditKey = "CREDIT";
  const pagoDespuesKey = "PAGO_DESPUES";

  if (line.group !== pagoDespuesGroup && line.group !== creditGroup) {
    return line;
  }

  const isCredit = line.group === creditGroup;
  const keyPrefix = isCredit ? creditKey : pagoDespuesKey;
  const pattern = new RegExp(`^${keyPrefix}_(\\d+)$`);

  const items = line.items;

  const matched = [];
  const matchedIndexSet = new Set();
  for (let i = 0; i < items.length; i++) {
    const m = String(items[i].line).match(pattern);
    if (m) {
      const num = Number(m[1]);
      matched.push({ item: items[i], num, index: i });
      matchedIndexSet.add(i);
    }
  }

  matched.sort((a, b) => a.num - b.num);

  const groups = [];
  let cur = null;
  for (const entry of matched) {
    if (!cur) {
      cur = { members: [entry] };
      continue;
    }
    const last = cur.members[cur.members.length - 1];
    const sameFee = entry.item.fee === last.item.fee;
    const sameTerm = entry.item.paymentTerm === last.item.paymentTerm;
    const consecutive = entry.num === last.num + 1;

    if (sameFee && sameTerm && consecutive) {
      cur.members.push(entry);
    } else {
      groups.push(cur);
      cur = { members: [entry] };
    }
  }
  if (cur) groups.push(cur);

  const indexToGroup = new Map();
  for (const g of groups) {
    for (const m of g.members) {
      indexToGroup.set(m.index, g);
    }
  }

  const emitted = new Set();
  const result = [];
  for (let i = 0; i < items.length; i++) {
    if (!indexToGroup.has(i)) {
      result.push(items[i]);
      continue;
    }
    const g = indexToGroup.get(i);
    if (emitted.has(g)) continue;
    emitted.add(g);

    if (g.members.length === 1) {
      result.push(g.members[0].item);
    } else {
      const start = g.members[0].num;
      const end = g.members[g.members.length - 1].num;
      const representative = g.members[0].item;
      result.push({
        line: `${keyPrefix}_${start}-${end}`,
        fee: representative.fee,
        paymentTerm: representative.paymentTerm,
        installments: [start, end],
      });
    }
  }

  return { ...line, items: result };
};

const renderDay = (paymentTerm) => {
  if (typeof paymentTerm === 'object') {
    const { min, max } = paymentTerm;
    if (min === max) {
      return i18n.get('Components.PlansModalV2.PlanMoreThanOneDayPaymentTerm')
        .replace('{days}', min);
    }
    return `${min} - ${max} ${i18n.get('Components.PlansModalV2.DaysLabel')}`;
  }

  if (paymentTerm === 0) {
    return i18n.get('Components.PlansModalV2.PlanTodayPaymentTerm');
  }
  return i18n.get('Components.PlansModalV2.PlanMoreThanOneDayPaymentTerm')
    .replace('{days}', paymentTerm);
}

  const calculateFee = (fee) => {
    const newFee = Number(fee) * 100
    return Utils.formatCurrency(newFee.toFixed(2));
  }

  const renderGroupName = (name) => {
    const text = i18n.get('Components.PlansModalV2.CategoryDisplayValues')[name];
    return text || name;
  }

  const renderPlanName = (name) => {
    const text = i18n.get('Components.PlansModalV2.PlansDisplayValues')[name];
    return text !== undefined ? text : name;
  }

const renderAdditionalServices = (services) => {
  if(!services) return
  const groupedServices = services.reduce((acc, item) => {
    if (!acc[item.group]) {
      acc[item.group] = [];
    }
    acc[item.group].push(item);
    return acc;
  }, {});

  const serviceData = Object.entries(groupedServices).map(([group, items]) => ({
    group,
    items
  }));
  return  serviceData?.map((service) => {
    return <div key={service.group}>
       <div className={classes.plansGroupTitle}>{service.group}</div>
       <div>
          {service.items.map ((item) => {
          return <div key={item.id} className={classes.field}>
              <div className={classes.plansGroupLine}>{item.name}</div>
                {item.displayType === 'amount' && <div>{i18n.get('Components.PlansModalV2.AdditionalServicePriceLabel')}{' '}<span className={classes.plansGroupLine}>{item.currency} {Utils.formatCurrency(item.amount)}</span></div>}
                {item.displayType === 'text' && <div><span className={classes.plansGroupLine}>{item.consultText}</span></div>}
            </div>
          })}
        </div>
      </div>
    })
}

  return (
    <Dialog open={open} onClose={onClose} classes={{ paper: classes.dialog }}>
      <DialogTitle className={classes.dialogTitleWrapper}>
        <div className={clsx(classes.dialogTitle, classes.dialogTitleWithCloseButton)}>
          {i18n.get('Components.PlansModalV2.ModalTitle')}
        </div>
        <div className={classes.dialogTitleClose} onClick={onClose}>
          <CancelOutlinedIcon className={classes.dialogTitleCloseIcon} color="inherit" />
        </div>
      </DialogTitle>
      <DialogContent className={classes.dialogContentWrapper}>
        {loading ? <div className={classes.loadingWrapper}><CircularProgress color="inherit" /></div> :
        <>
          <Accordion defaultExpanded className={classes.plansWrapper}>
            <AccordionSummary expandIcon={<ExpandMoreIcon/>} classes={{ expanded: classes.accordionSummaryExpanded}}>
              <div className={classes.plansPlan712AdditionalsTitle}>
                {i18n.get('Components.PlansModalV2.AccordionItemsSummaries.Summary1')}
              </div>
            </AccordionSummary>
            <AccordionDetails className={classes.plansLinesAccordion}>
              <div className={classes.accordionContent}>
                {plan ? <><div>{i18n.get('Components.PlansModalV2.PlanInputLabel')}</div>
                  <div className={classes.field}>{plan?.publicName}</div>
                  {plan?.lines.map((line) => {
                    return (
                      renderGroup(line)
                    );
                })}</> : <div className={classes.field}>{i18n.get('Components.PlansModalV2.InformationNotAvailable')}</div>}
              </div>
            </AccordionDetails>
          </Accordion>
          <Accordion className={classes.plansWrapper} >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} classes={{ expanded: classes.accordionSummaryExpanded}}>
              <div className={classes.plansPlan712AdditionalsTitle}>
                {i18n.get('Components.PlansModalV2.AccordionItemsSummaries.Summary2')}
              </div>
            </AccordionSummary>
            <AccordionDetails className={classes.plansLinesAccordion}>
              <div className={classes.coefficientContent}>
                {organizedCoefficientData ? organizedCoefficientData?.map(item => (
                  <div key={item.installments} className={classes.field}>
                    <div className={classes.plansGroupLine}>{item.installments} {i18n.get('Components.PlansModalV2.CoefficientInstallmentsLabel')}</div>
                    <div className={classes.plansGroupItem}>
                      <div>{i18n.get('Components.PlansModalV2.CoefficientUYULabel')} <span className={classes.plansGroupLine}>{Utils.formatCoefficient(item.UYU)}</span></div>
                      <div>{i18n.get('Components.PlansModalV2.CoefficientUSDLabel')} <span className={classes.plansGroupLine}>{Utils.formatCoefficient(item.USD)}</span></div>
                    </div>
                  </div>
                )) : <div className={classes.field}>{i18n.get('Components.PlansModalV2.InformationNotAvailable')}</div>}
                <Markdown className={classes.disclaimerText}>
                  {i18n.get('Components.PlansModalV2.CoefficientAdditionalText')}
                </Markdown>
              </div>
            </AccordionDetails>
          </Accordion>
          <Accordion className={classes.plansWrapper}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} classes={{ expanded: classes.accordionSummaryExpanded}}>
              <div className={classes.plansPlan712AdditionalsTitle}>
                {i18n.get('Components.PlansModalV2.AccordionItemsSummaries.Summary3')}
              </div>
            </AccordionSummary>
            <AccordionDetails className={classes.plansLinesAccordion}>
                <div className={classes.accordionContent}>
                  {additionalServiceData ? renderAdditionalServices(additionalServiceData) : <div className={classes.field}>{i18n.get('Components.PlansModalV2.InformationNotAvailable')}</div>}
                  <Markdown className={classes.disclaimerText}>
                    {i18n.get(hasRUT ? 'Components.PlansModalV2.AdditionalServiceIPJText' : 'Components.PlansModalV2.AdditionalServiceIPEText')}
                  </Markdown>
                </div>
            </AccordionDetails>
          </Accordion>
        </>}
      </DialogContent>
    </Dialog>
  );
};

export default NewPlansModal;
